import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { AutoSizer, List } from 'react-virtualized';
import BoardItem from './BoardItem/BoardItem';
import ActionType from '../../redux/actionType';
import './index.scss';
import { RootState } from '../../redux/store';
import { actionSendSocketData } from '../../redux/action';
import { formatConnectLostAndExpGroup } from '../../views/RealtimeView/util';
import { isEmpty } from '../../utils';
import {
  addGlobalEventListener,
  removeGlobalEventListener,
} from '@/utils/emit';
export type GroupItem = {
  label: string;
  groupKey: string;
  boardList: any[];
  pageSize?: number;
  total?: number;
};

type BoardRow =
  | { type: 'header'; groupKey: string; label: string }
  | { type: 'cards'; groupKey: string; boardList: any[] }
  | { type: 'load-more'; groupKey: string; remaining: number };

type VirtualListRef = {
  forceUpdateGrid: () => void;
  recomputeRowHeights: () => void;
};

const CARD_WIDTH = 366;
const CARD_ROW_HEIGHT = 220;
const GROUP_HEADER_HEIGHT = 60;
const LOAD_MORE_ROW_HEIGHT = 44;
const PAGE_SIZE = 20;
const PAGE_REQUEST_TIMEOUT = 10000;
const INITIAL_PAGE_LIST = [
  'CONNECTION_LOST',
  'VEHICLE_CRASH',
  'SENSOR_ERROR',
  'OPERATION_ALARM',
  'BOOT_ALARM',
  'STOP',
  'LOW_BATTERY',
  'LOW_PRESSURE',
  'INTERSECTION_ABNORMAL',
].map((category) => ({ category, pageNum: 1, pageSize: PAGE_SIZE }));

export const buildBoardRows = (
  groupData: GroupItem[],
  columnCount: number,
): BoardRow[] => {
  const rows: BoardRow[] = [];

  groupData.forEach(({ label, groupKey, boardList, total }) => {
    if (isEmpty(boardList)) {
      return;
    }

    rows.push({ type: 'header', groupKey, label });
    for (let index = 0; index < boardList.length; index += columnCount) {
      rows.push({
        type: 'cards',
        groupKey,
        boardList: boardList.slice(index, index + columnCount),
      });
    }
    const remaining = (total || 0) - boardList.length;
    if (remaining > 0) {
      rows.push({ type: 'load-more', groupKey, remaining });
    }
  });

  return rows;
};

export const getVisibleLoadMoreGroupKeys = (
  boardRows: BoardRow[],
  startIndex: number,
  stopIndex: number,
) =>
  boardRows
    .slice(startIndex, stopIndex + 1)
    .filter((row): row is Extract<BoardRow, { type: 'load-more' }> =>
      row.type === 'load-more',
    )
    .map((row) => row.groupKey);

export const refreshVirtualList = (list: VirtualListRef | null) => {
  list?.recomputeRowHeights();
  list?.forceUpdateGrid();
};

type BoardGroupProp = {
  updateGroupTotal: AnyFunc;
  searchCondition: any;
  cockpitNum: any;
  handleClickStopItem: AnyFunc;
  handleScheduleModel: AnyFunc;
  activeSubTabId?: string;
};
let timer: any = null;
const BoadGroup = (props: BoardGroupProp) => {
  const dispatch = useDispatch();
  const common = useSelector((state: RootState) => state.common);
  const cockpitInfo = useSelector((state: RootState) => state.cockpitInfo);
  const listRef = useRef<List>(null);
  const pendingPageRequest = useRef<{
    key: string;
    startedAt: number;
  } | null>(null);
  const [groupData, setGroupData] = useState<GroupItem[]>([]);
  const [columnCount, setColumnCount] = useState(1);
  const { realTimeBoardData } = common;
  const { currentCockpit } = cockpitInfo;
  const fetchBoardList = (pageList: any[], cockpitNumber?: any) => {
    const data = {
      eventType: ActionType.MULTI_VEHICLE,
      data: {
        ...props.searchCondition,
        cockpitNumber: cockpitNumber === 'user' ? null : cockpitNumber,
        multiVehiclePageList: pageList,
      },
    };

    dispatch(actionSendSocketData(JSON.stringify(data)));
  };

  useEffect(() => {
    if (!props.cockpitNum) {
      return;
    }
    fetchBoardList(INITIAL_PAGE_LIST, props.cockpitNum);
  }, [props.cockpitNum]);

  useEffect(() => {
    const boardListData = formatConnectLostAndExpGroup(
      realTimeBoardData,
      common.vehicle,
    );
    const responseGroups = realTimeBoardData.multiVehiclePageList || [];

    setGroupData((currentGroups) => {
      const nextGroups = new Map(
        currentGroups.map((group) => [group.groupKey, group]),
      );

      responseGroups.forEach((responseGroup: BoardGroupItem) => {
        const formattedGroup: any = boardListData.get(responseGroup.category);
        if (!formattedGroup) {
          return;
        }

        const previous = nextGroups.get(responseGroup.category);
        const nextBoardList = previous ? [...previous.boardList] : [];
        const pageOffset = (responseGroup.pageNum - 1) * responseGroup.pageSize;

        nextBoardList.splice(
          pageOffset,
          responseGroup.pageSize,
          ...formattedGroup.data,
        );
        nextGroups.set(responseGroup.category, {
          label: formattedGroup.label,
          groupKey: responseGroup.category,
          boardList: nextBoardList.slice(0, responseGroup.total),
          pageSize: responseGroup.pageSize,
          total: responseGroup.total,
        });
        if (
          pendingPageRequest.current?.key ===
          `${responseGroup.category}-${responseGroup.pageNum}`
        ) {
          pendingPageRequest.current = null;
        }
      });

      return Array.from(boardListData.keys())
        .map((groupKey) => nextGroups.get(groupKey))
        .filter((group): group is GroupItem => Boolean(group));
    });

    if (responseGroups.length === INITIAL_PAGE_LIST.length) {
      props.updateGroupTotal(boardListData);
    }
  }, [realTimeBoardData]);

  useEffect(() => {
    const refreshFirstPages = () => {
      if (pendingPageRequest.current) {
        timer = setTimeout(refreshFirstPages, 500);
        return;
      }
      if (document.hidden) {
        timer = setTimeout(refreshFirstPages, 1000);
        return;
      }

      clearTimeout(timer);
      fetchBoardList(INITIAL_PAGE_LIST, currentCockpit.cockpitNumber);
    };

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        clearTimeout(timer);
        timer = setTimeout(refreshFirstPages, 500);
      }
    };

    timer = setTimeout(refreshFirstPages, 3000);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      timer && clearTimeout(timer);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [realTimeBoardData.timeStamp, currentCockpit?.cockpitNumber]);

  const handleLoadMore = useCallback(
    (groupKey: string) => {
      const group = groupData.find((item) => item.groupKey === groupKey);
      const nextPage = Math.floor((group?.boardList.length || 0) / PAGE_SIZE) + 1;
      const requestKey = `${groupKey}-${nextPage}`;

      if (!group) {
        return;
      }

      const pendingRequest = pendingPageRequest.current;
      if (
        pendingRequest &&
        Date.now() - pendingRequest.startedAt < PAGE_REQUEST_TIMEOUT
      ) {
        return;
      }

      pendingPageRequest.current = { key: requestKey, startedAt: Date.now() };
      fetchBoardList(
        [{ category: groupKey, pageNum: nextPage, pageSize: PAGE_SIZE }],
        currentCockpit.cockpitNumber,
      );
    },
    [currentCockpit.cockpitNumber, groupData],
  );

  const boardRows = useMemo(
    () => buildBoardRows(groupData, columnCount),
    [columnCount, groupData],
  );
  const groupRowIndexes = useMemo(() => {
    const indexes = new Map<string, number>();

    boardRows.forEach((row, index) => {
      if (row.type === 'header') {
        indexes.set(row.groupKey, index);
      }
    });
    return indexes;
  }, [boardRows]);
  const handleResize = useCallback(({ width }: { width: number }) => {
    const nextColumnCount = Math.max(1, Math.floor((width + 16) / CARD_WIDTH));

    setColumnCount((currentColumnCount) =>
      currentColumnCount === nextColumnCount
        ? currentColumnCount
        : nextColumnCount,
    );
  }, []);

  const handleRowsRendered = useCallback(
    ({ startIndex, stopIndex }: { startIndex: number; stopIndex: number }) => {
      const groupKeys = getVisibleLoadMoreGroupKeys(
        boardRows,
        startIndex,
        stopIndex,
      );
      const groupKey = groupKeys[groupKeys.length - 1];

      if (groupKey) {
        handleLoadMore(groupKey);
      }
    },
    [boardRows, handleLoadMore],
  );

  useEffect(() => {
    refreshVirtualList(listRef.current);
  }, [boardRows]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        window.requestAnimationFrame(() => refreshVirtualList(listRef.current));
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    const rowIndex = props.activeSubTabId
      ? groupRowIndexes.get(props.activeSubTabId)
      : undefined;

    if (rowIndex !== undefined) {
      listRef.current?.scrollToRow(rowIndex);
    }
  }, [groupRowIndexes, props.activeSubTabId]);

  const renderRow = useCallback(
    ({ index, key, style }: any) => {
      const row = boardRows[index];

      if (row.type === 'header') {
        return (
          <div
            className='board-group-header'
            id={row.groupKey}
            key={key}
            style={style}
          >
            <div className='label'>
              <i></i>
              {row.label}
            </div>
          </div>
        );
      }

      if (row.type === 'load-more') {
        return (
          <div className='board-group-load-more' key={key} style={style}>
            正在加载更多（剩余 {row.remaining} 辆）
          </div>
        );
      }

      return (
        <ul className='board-list board-list-row' key={key} style={style}>
          {row.boardList.map((boardItem) => (
            <BoardItem
              key={boardItem.id}
              data={boardItem}
              handleClickStopItem={props.handleClickStopItem}
              handleScheduleModel={() => {
                props.handleScheduleModel(boardItem.license);
              }}
            />
          ))}
        </ul>
      );
    },
    [boardRows, handleLoadMore, props.handleClickStopItem, props.handleScheduleModel],
  );

  return (
    <div className='board-group'>
      <AutoSizer onResize={handleResize}>
        {({ height, width }) => (
          <List
            ref={listRef}
            height={height}
            onRowsRendered={handleRowsRendered}
            rowCount={boardRows.length}
            rowHeight={({ index }) =>
              boardRows[index].type === 'header'
                ? GROUP_HEADER_HEIGHT
                : boardRows[index].type === 'load-more'
                  ? LOAD_MORE_ROW_HEIGHT
                  : CARD_ROW_HEIGHT
            }
            rowRenderer={renderRow}
            width={width}
          />
        )}
      </AutoSizer>
    </div>
  );
};

export default React.memo(BoadGroup);
